/**
 * EditorFullscreenButton — sl-e0il
 *
 * Icon-only `<IconButton>` that opens `EditorFullscreenDialog` containing
 * a second instance of the same editor. The inline and dialog editors are
 * controlled components sharing `value` / `onChange`, so edits in either
 * side stay in sync.
 *
 * The dialog editor gets its own `EditorAdapter` ref so the optional
 * `<TemplateInsertMenu>` inside the dialog operates on the dialog's
 * CodeMirror instance, not the inline one behind the overlay. The
 * autocomplete popup portals into the dialog content element (via
 * `tooltipsParent`) so it renders inside the modal layer rather than
 * behind it.
 *
 * Variants:
 *   - `variant: 'template'` → renders `<LazyTemplateEditor>` inside
 *   - `variant: 'code'`     → renders `<LazyCodeEditor>` with a language
 */

import { IconButton } from '@affine/component/ui/button';
import { Modal } from '@affine/component/ui/modal';
import { useI18n } from '@affine/i18n';
import { ExpandFullIcon } from '@blocksuite/icons/rc';
import type { Extension } from '@codemirror/state';
import { useCallback, useRef, useState } from 'react';

import type { CodeEditorLanguage } from './code-editor';
import * as styles from './editor.css';
import type { EditorAdapter } from './editor-adapter';
import { EditorFormatButton } from './editor-format-button';
import type { InsertableCredential, InsertableProvider } from './insertable';
import { LazyCodeEditor, LazyTemplateEditor } from './lazy';
import { TemplateInsertMenu } from './template-insert-menu';

/**
 * Config for the optional `<TemplateInsertMenu>` rendered alongside
 * the editor inside the fullscreen dialog. Passing `undefined` omits
 * the menu — useful for standalone code editors that don't need
 * project-specific variable insertion.
 */
export interface FullscreenInsertMenuConfig {
  provider: InsertableProvider;
  variablesEmptyLabel?: string;
  onBeforeCredentialInsert?: (
    credential: InsertableCredential
  ) => Promise<boolean>;
}

type BaseProps = {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  extensions?: readonly Extension[];
  /** Modal title — defaults to a generic "Editor" label. */
  title?: string;
  /** Disable the trigger button. */
  disabled?: boolean;
  /** Config for the `<TemplateInsertMenu>` rendered inside the dialog. */
  insertMenu?: FullscreenInsertMenuConfig;
};

export type EditorFullscreenButtonProps =
  | (BaseProps & { variant: 'template' })
  | (BaseProps & { variant: 'code'; language: CodeEditorLanguage });

export function EditorFullscreenButton(props: EditorFullscreenButtonProps) {
  const [open, setOpen] = useState(false);
  const dialogEditorRef = useRef<EditorAdapter | null>(null);
  // Host for the CodeMirror tooltip portal inside the Modal tree —
  // see `fullscreenTooltipHost` in `editor.css.ts` for the rationale.
  // Tracked in state (not just a ref) because the child editor needs
  // a re-render once the host element is attached so the `tooltips()`
  // extension can be reconfigured with the concrete parent.
  const [tooltipHostEl, setTooltipHostEl] = useState<HTMLDivElement | null>(
    null
  );
  const tooltipHostRef = useCallback((el: HTMLDivElement | null) => {
    setTooltipHostEl(el);
  }, []);
  const t = useI18n();

  const label =
    t['com.streamlain.streamlain.workflow.editor.fullscreen.trigger']();
  const defaultTitle =
    t['com.streamlain.streamlain.workflow.editor.fullscreen.title']();

  return (
    <>
      <IconButton
        variant="plain"
        size="20"
        onClick={() => setOpen(true)}
        disabled={props.disabled}
        aria-label={label}
        tooltip={label}
        icon={<ExpandFullIcon />}
      />
      <Modal
        open={open}
        onOpenChange={setOpen}
        title={props.title ?? defaultTitle}
        width="90vw"
        height="85vh"
        disableAutoFocus
        // `animation="none"` — the default `fadeScaleTop` animation uses
        // `animationFillMode: forwards`, leaving `transform: scale(1)`
        // on the modal content wrapper after the open animation finishes.
        // A non-none transform creates a containing block for fixed
        // descendants, which would clip CodeMirror's default tooltip
        // (`position: fixed`, anchored inside `view.dom`) to the Modal
        // bounds and break the autocomplete popup.
        animation="none"
        contentOptions={{
          style: {
            maxWidth: '1400px',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <div className={styles.fullscreenEditorWrapper}>
          <div ref={tooltipHostRef} className={styles.fullscreenTooltipHost} />
          {props.insertMenu || props.variant === 'code' ? (
            <div className={styles.fullscreenInsertMenuRow}>
              {props.insertMenu ? (
                <TemplateInsertMenu
                  editorRef={dialogEditorRef}
                  provider={props.insertMenu.provider}
                  disabled={props.readOnly}
                  variablesEmptyLabel={props.insertMenu.variablesEmptyLabel}
                  onBeforeCredentialInsert={
                    props.insertMenu.onBeforeCredentialInsert
                  }
                />
              ) : null}
              {props.variant === 'code' ? (
                <EditorFormatButton
                  value={props.value}
                  onChange={props.onChange}
                  language={props.language}
                  readOnly={props.readOnly}
                />
              ) : null}
            </div>
          ) : null}
          {props.variant === 'code' ? (
            <LazyCodeEditor
              ref={dialogEditorRef}
              value={props.value}
              onChange={props.onChange}
              language={props.language}
              readOnly={props.readOnly}
              placeholder={props.placeholder}
              extensions={props.extensions}
              tooltipsParent={tooltipHostEl}
              fill
            />
          ) : (
            <LazyTemplateEditor
              ref={dialogEditorRef}
              value={props.value}
              onChange={props.onChange}
              readOnly={props.readOnly}
              placeholder={props.placeholder}
              extensions={props.extensions}
              tooltipsParent={tooltipHostEl}
              fill
            />
          )}
        </div>
      </Modal>
    </>
  );
}
